import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@byscott-io/corebyscott';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apps, products, productLevels } from '../api/client';

const BILLING_TYPES = [
  { value: 'flat', label: 'Flat Rate', description: 'One price for all users' },
  { value: 'per_user', label: 'Per User', description: 'Base price + per additional user' },
  { value: 'usage', label: 'Usage Based', description: 'Pay for what you use' },
  { value: 'tiered', label: 'Tiered', description: 'Price varies by usage tier' },
];

export default function ProductDetailPage() {
  const { appId, productId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCreateLevelModal, setShowCreateLevelModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [appRes, productRes] = await Promise.all([
        apps.get(appId),
        products.get(appId, productId),
      ]);
      setApp(appRes.data.app);
      setProduct(productRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [appId, productId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async () => {
    try {
      await products.delete(appId, productId);
      navigate(`/app/apps/${appId}/products`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete product');
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-500">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="mb-6 text-sm">
        <Link to="/app/apps" className="text-blue-600 hover:text-blue-800">Apps</Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to={`/app/apps/${appId}`} className="text-blue-600 hover:text-blue-800">{app?.name}</Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to={`/app/apps/${appId}/products`} className="text-blue-600 hover:text-blue-800">Products</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-600">{product?.name}</span>
      </div>

      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold">{product.name}</h1>
          <p className="text-gray-500">/{product.slug}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Edit
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Product Details */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Status</label>
            <div className="mt-1">
              {product.active ? (
                <span className="px-2 py-1 text-sm rounded-full bg-green-100 text-green-800">Active</span>
              ) : (
                <span className="px-2 py-1 text-sm rounded-full bg-gray-100 text-gray-800">Inactive</span>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Description</label>
            <p className="mt-1 text-gray-900">{product.description || '—'}</p>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-500">Default Trial Level</label>
            <div className="mt-1">
              {product.default_trial_product_level ? (
                <span className="text-gray-900">
                  {product.default_trial_product_level.name}
                  {product.default_trial_product_level.trial_days > 0 && (
                    <span className="text-green-600 ml-2">
                      ({product.default_trial_product_level.trial_days} day trial)
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-gray-500 italic">None — new accounts won&apos;t auto-receive trials for this product</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Levels */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Pricing Levels</h2>
        <button
          onClick={() => setShowCreateLevelModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          + Add Level
        </button>
      </div>

      {product.levels?.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <p className="text-gray-500 mb-4">No pricing levels yet. Add your first level.</p>
          <button
            onClick={() => setShowCreateLevelModal(true)}
            className="text-blue-600 hover:text-blue-800"
          >
            Add a level
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {product.levels?.map((level) => (
            <Link
              key={level.id}
              to={`/app/apps/${appId}/products/${productId}/levels/${level.id}`}
              className="bg-white shadow rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">{level.name}</h3>
                    <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">
                      {BILLING_TYPES.find(t => t.value === level.billing_type)?.label || level.billing_type}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">/{level.slug}</p>
                </div>
                <div className="text-right">
                  {level.prices && level.prices.length > 0 ? (
                    <div className="space-y-1">
                      {level.prices.map((price) => {
                        const isPerUser = level.billing_type === 'per_user';
                        const amount = isPerUser ? price.price_per_user_cents : price.price_cents;
                        return (
                          <div key={price.billing_interval}>
                            <span className="text-lg font-bold">
                              ${((amount || 0) / 100).toFixed(2)}
                            </span>
                            <span className="text-sm text-gray-500">
                              {isPerUser ? '/user' : ''}/{price.billing_interval}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No pricing set</p>
                  )}
                  {level.trial_days > 0 && (
                    <p className="text-sm text-green-600">{level.trial_days} day trial</p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {editing && (
        <EditProductModal
          appId={appId}
          product={product}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            loadData();
          }}
        />
      )}

      <Modal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Product"
        size="md"
        footer={(
          <>
            <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button onClick={handleDelete} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">
              Delete
            </button>
          </>
        )}
      >
        <p className="text-gray-600">
          Are you sure you want to delete <strong>{product.name}</strong>? This will also delete all levels.
        </p>
      </Modal>

      {showCreateLevelModal && (
        <CreateLevelModal
          appId={appId}
          productId={productId}
          onClose={() => setShowCreateLevelModal(false)}
          onCreated={() => {
            setShowCreateLevelModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function EditProductModal({ appId, product, onClose, onSaved }) {
  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(product.description || '');
  const [active, setActive] = useState(product.active);
  const [defaultTrialLevelId, setDefaultTrialLevelId] = useState(
    product.default_trial_product_level_id || ''
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      await products.update(appId, product.id, {
        name,
        description,
        active,
        default_trial_product_level_id: defaultTrialLevelId || null,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Edit Product" size="md">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Trial Level</label>
            <select
              value={defaultTrialLevelId}
              onChange={(e) => setDefaultTrialLevelId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">None (no auto-trial)</option>
              {product.levels?.map((level) => (
                <option key={level.id} value={level.id}>
                  {level.name} {level.trial_days > 0 ? `(${level.trial_days} day trial)` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              New client accounts will automatically receive a trial subscription at this level.
            </p>
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
    </Modal>
  );
}

function CreateLevelModal({ appId, productId, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [billingType, setBillingType] = useState('flat');
  const [trialDays, setTrialDays] = useState('0');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      await productLevels.create(appId, productId, {
        name,
        billing_type: billingType,
        trial_days: parseInt(trialDays || 0),
      });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create level');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Add Pricing Level" size="lg">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Level Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Starter, Pro, Enterprise"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Billing Type</label>
            <select
              value={billingType}
              onChange={(e) => setBillingType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {BILLING_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label} - {type.description}
                </option>
              ))}
            </select>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            Pricing is configured after creation in the level&apos;s Pricing tab.
          </p>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Trial Days</label>
            <input
              type="number"
              min="0"
              value={trialDays}
              onChange={(e) => setTrialDays(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Level'}
            </button>
          </div>
        </form>
    </Modal>
  );
}
